import { MapLike, MaxMin } from '../../core'

export type HistoryStoreSystemAtts = {
  maxAgeMS: number | undefined
  maxLength: number | undefined
  autoTrim: boolean
}

export type HistoryStoreProtextedKeys = string[] //['timestamp', 'index', 'systemProperties', 'protectedKeys', 'statistics']

export type HistoryStoreClassKeys = {
  protectedKeys: HistoryStoreProtextedKeys
  index: number[]
  timestamp: number[]
  statistics?: ICMVarAggregationMapType
  systemProperties?: HistoryStoreSystemAtts
}

export type HistoryObjectType = Partial<HistoryStoreSystemAtts> &
  Record<string, any[] | Partial<HistoryStoreSystemAtts> | ICMVarAggregationMapType> & { // the metric key, followed by an array of numbers
    index: number[] // 0, 1, 2, 3
    timestamp: number[] // timestamp as number in ms eg new Date().valueOf()
    systemProperties?: Partial<HistoryStoreSystemAtts>
    statistics?: ICMVarAggregationMapType
    //
  }

// 2024 11 - part of improving how calculate state types are tied history store type
/** Given T, enforces key arrays in History Store */
type TypedHistoryStoreArrays<T extends { [k: string]: any }> = {
  [k in keyof T]: T[k][]
}

/** enforces keys of T as data arrays in HistoryStore */
export type TypedHistoryStore<T extends { [k: string]: any }> = Partial<HistoryStoreSystemAtts> & HistoryStoreClassKeys & TypedHistoryStoreArrays<T>

const x: TypedHistoryStore<{ y: number | null }> = {
  index: [],
  protectedKeys: ['timestamp', 'index', 'systemProperties', 'protectedKeys', 'statistics'],
  timestamp: [],
  autoTrim: false,
  y: [null],
}

// StateSnapshot.state should not have keys timestamp or index otheriwse will backfill those hidden keys in HistoryObject: Possibly resolved
export type StateSnapshot<T> = {
  timestamp: number
  state: MapLike<T>
}

export type TypedStateSnapshot<T extends { [k: string]: any }> = {
  timestamp: number
  state: { [k in keyof Omit<T, 'timestamp' | 'index' | 'systemProperties' | 'protectedKeys' | 'statistics'>]: T[k] }
}

export type AttributeSnapshot = {
  timestamp: number
  key: string
  value: any
}

export type IndexRange = [number, number]
export type DateRange = [from: Date, to: Date]

export const sortDateRange = (drA: DateRange, drB: DateRange) => (drA < drB ? -1 : 1)

export type EnabledDateRange = { enabled: boolean; dateRange: DateRange }

export type DataTableZombie = [any[]]

export interface IHistoryObjectFunctions {
  push: (snapshot: StateSnapshot<any>) => HistoryObjectType
  pop: () => StateSnapshot<any> | undefined
  shift: () => StateSnapshot<any> | undefined
  unshift: (snapshot: StateSnapshot<any>) => HistoryObjectType

  sliceOneKey?: (range: IndexRange, key: string) => HistoryObjectType
  dateliceOneKey?: (dateRange: DateRange, key: string) => HistoryObjectType

  slice: (range: IndexRange) => HistoryObjectType
  dateSlice?: (dateRange: DateRange) => HistoryObjectType

  firstIndexAfter?: (start: Date) => number | undefined
  lastIndexBefore?: (end: Date) => number | undefined

  getLength: () => number
  getPeriod?: () => DateRange

  toDataTable?: () => DataTableZombie

  keys: () => string[]
  hasKey: (k: string) => boolean
}

export interface ICMVarAggregationMapType {
  data: { [k: string]: CMVarAggregationType }
}

/**
 * @type CMVarAggregationLiteType Arrayized version of CMVarAggregationType to persist and rehydate. Null are used as undefined elements are removed in DynamoDB and possibly S3
 */
export type CMVarAggregationLiteType = [cn: number | null, mean: number | null, sqDiffFromMean: number | null, max: number | null, min: number | null]

export type CMVarAggregationType = {
  firstTimestamp: number | undefined
  lastTimeStamp: number | undefined
  cn: number
  mean: number
  sqDiffFromMean: number
  max?: number
  min?: number
}

export type MetricStatsType = {
  firstTimestamp: number | undefined
  lastTimeStamp: number | undefined
  count: number
  mean: number
  varPop: number
  varSample: number
  sdPop: number
  sdSample: number
  max: number | undefined
  min: number | undefined
}

export const getPassLevel = (stats: MapLike<MetricStatsType>, maxMin: MaxMin, key: string, stdDevs: number = 1) => {
  let level: number = maxMin === MaxMin.MAX ? Infinity : 0
  if (stats[key]) {
    const delta = stats[key].sdPop
    level = stats[key].mean + stdDevs * (maxMin === MaxMin.MAX ? delta : -delta)
  }
  return level
}

export const CMVArrayToMap = (cmvA: CMVarAggregationLiteType): CMVarAggregationType => {
  const keys: (keyof Exclude<CMVarAggregationType, 'firstTimestamp' | 'lastTimeStamp'>)[] = ['cn', 'mean', 'sqDiffFromMean', 'max', 'min']

  const hydrated = Object.fromEntries(keys.map((k, i) => [k, cmvA[i]])) as CMVarAggregationType

  // Treat count of 1 and array of four nulls as 0's (I think zipping the accumulator might turn a string of four zeroes into all nulls)
  if (hydrated.cn === 1 && hydrated.mean === null) {
    hydrated.mean = 0
    hydrated.max = 0
    hydrated.min = 0
    hydrated.sqDiffFromMean = 0
  }

  return hydrated
}

export const CMVMapToArray = (cmvMap: CMVarAggregationType): CMVarAggregationLiteType => {
  const keys: (keyof Exclude<CMVarAggregationType, 'firstTimestamp' | 'lastTimeStamp'>)[] = ['cn', 'mean', 'sqDiffFromMean', 'max', 'min']

  const cmvArr: CMVarAggregationLiteType = (() => new Array(keys.length))().fill(null) as CMVarAggregationLiteType

  keys.forEach((k, i) => {
    cmvArr[i] = cmvMap[k] ?? null
  })

  return cmvArr
}
